import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronsLeftRight, ChevronsRightLeft, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CategoryChip } from "@/components/app/ScoreBits";
import { cn } from "@/lib/utils";
import { CredBadge, BarMissingChip } from "@/pages/candidates/CredentialsSection";

function KCard({ r, stages, canEdit, onMove }) {
  const nav = useNavigate();
  const open = () => nav(`/app/candidates/${r.candidate_id}/overview`, { state: { name: r.name } });
  return (
    <motion.div layout layoutId={`kanban-${r.application_id}`} transition={{ type: "spring", stiffness: 480, damping: 38 }}
      draggable={canEdit} onDragStart={(e) => e.dataTransfer.setData("text/plain", r.application_id)} onClick={open}
      onKeyDown={(e) => e.key === "Enter" && open()} tabIndex={0} role="link" aria-label={`Open ${r.name}`}
      className="tl-lift cursor-pointer space-y-1.5 rounded-[6px] border border-line bg-white p-2.5 text-sm shadow-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand" data-testid={`kanban-card-${r.application_id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="truncate font-medium text-ink">{r.name}</p>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="text-ink-muted hover:text-ink" aria-label="Move to" data-testid={`kanban-move-menu-${r.application_id}`}><MoreHorizontal size={16} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {stages.filter(([k]) => k !== r.stage).map(([k, l]) => <DropdownMenuItem key={k} onClick={() => onMove([r.application_id], k)} data-testid={`kanban-move-${r.application_id}-${k}`}>Move to {l}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex items-center justify-between gap-2"><CategoryChip category={r.category} overridden={r.overridden} /><span className="font-display font-semibold tabular-nums">{r.resume_match ?? "—"}</span></div>
      {(r.creds?.total > 0 || r.bar_missing) && <div className="flex flex-wrap gap-1"><CredBadge s={r.creds} testId={`kanban-cred-badge-${r.application_id}`} />{r.bar_missing && <BarMissingChip testId={`kanban-bar-missing-${r.application_id}`} />}</div>}
    </motion.div>
  );
}

export function KanbanColumn({ k, label, items, collapsed, onToggle, over, dropProps, cardProps }) {
  if (collapsed) {
    return (
      <button type="button" onClick={onToggle} data-testid={`kanban-col-${k}`} data-collapsed="true" {...dropProps} aria-label={`Expand ${label} (${items.length})`}
        className={cn("tl-lift flex w-11 shrink-0 snap-start flex-col items-center gap-3 rounded-[6px] border py-3", over ? "border-brand bg-brand-tint" : "border-line bg-subtle")}>
        <ChevronsLeftRight size={14} className="text-ink-muted" />
        <span className="rounded-full bg-white px-1.5 text-xs font-semibold tabular-nums text-ink" data-testid={`kanban-col-count-${k}`}>{items.length}</span>
        <span className="text-xs font-medium text-ink-secondary [writing-mode:vertical-rl]">{label}</span>
      </button>
    );
  }
  return (
    <div data-testid={`kanban-col-${k}`} {...dropProps}
      className={cn("flex w-[200px] min-w-[176px] flex-1 shrink-0 snap-start flex-col gap-2 rounded-[6px] border p-2.5 transition-colors duration-150 xl:w-auto", over ? "border-brand bg-brand-tint" : "border-line bg-subtle")}>
      <div className="flex items-center justify-between gap-1 px-0.5 text-sm font-medium">
        <span className="truncate">{label}</span>
        <span className="flex items-center gap-1">
          <span className="text-xs tabular-nums text-ink-muted" data-testid={`kanban-col-count-${k}`}>{items.length}</span>
          <button type="button" onClick={onToggle} aria-label={`Collapse ${label}`} data-testid={`kanban-col-collapse-${k}`} className="rounded p-0.5 text-ink-muted hover:bg-white hover:text-ink"><ChevronsRightLeft size={14} /></button>
        </span>
      </div>
      {items.map((r) => <KCard key={r.application_id} r={r} {...cardProps} />)}
      {!items.length && <p className="py-6 text-center text-xs text-ink-muted">Nobody here yet</p>}
    </div>
  );
}
